<template>
  <div class="schedule-edit-page">
    <div class="container">
      <h1 class="title">Edit Schedule</h1>
      <h2 class="subtitle" v-if="site.name">{{ site.name }}</h2>

      <div class="box">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <router-link :to="`/schedules/${scheduleId}`" class="button is-info">
                <span class="icon"><i class="bx bx-arrow-left"></i></span>
                <span>Back to Schedule</span>
              </router-link>
            </div>
          </div>
        </div>

        <div v-if="loading" class="has-text-centered my-5">
          <button class="button is-loading is-large is-text">Loading</button>
          <p>Loading schedule data...</p>
        </div>

        <form v-else @submit.prevent="saveSchedule">
          <!-- Working Days Section -->
          <div class="field">
            <label class="label">Working Days</label>
            <div class="box">
              <div v-for="(day) in formData.working_days" :key="day.day_of_week" class="columns is-vcentered mb-2">
                <div class="column is-2">
                  <div class="field has-addons">
                    <p class="control">
                      <label class="checkbox">
                        <input
                            type="checkbox"
                            v-model="day.is_working"
                        >
                        {{ getDayName(day.day_of_week) }}
                      </label>
                    </p>
                  </div>
                </div>
                <div class="column">
                  <div class="columns" v-if="day.is_working">
                    <div class="column">
                      <div class="field">
                        <label class="label is-small">Open Time</label>
                        <div class="control">
                          <input
                              type="time"
                              class="input"
                              v-model="day.open_time"
                          >
                        </div>
                      </div>
                    </div>
                    <div class="column">
                      <div class="field">
                        <label class="label is-small">Close Time</label>
                        <div class="control">
                          <input
                              type="time"
                              class="input"
                              v-model="day.close_time"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                  <p v-else class="has-text-grey">Non-working day</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Working Months Section -->
          <div class="field">
            <label class="label">Working Months</label>
            <div class="box">
              <div class="columns is-multiline">
                <div v-for="month in formData.working_months" :key="month.month" class="column is-3">
                  <div class="field">
                    <div class="control">
                      <label class="checkbox">
                        <input
                            type="checkbox"
                            v-model="month.is_working"
                        >
                        {{ getMonthName(month.month) }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Holidays Section -->
          <div class="field">
            <label class="label">Holidays</label>
            <div class="box">
              <div v-for="(holiday, index) in formData.holidays" :key="holiday.id || index" class="columns is-vcentered mb-2">
                <div class="column">
                  <div class="field">
                    <div class="control">
                      <input
                          type="text"
                          class="input"
                          placeholder="Holiday Name"
                          v-model="holiday.name"
                          required
                      >
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="field">
                    <div class="control">
                      <input
                          type="date"
                          class="input"
                          v-model="holiday.date"
                          required
                      >
                    </div>
                  </div>
                </div>
                <div class="column is-3">
                  <div class="field">
                    <div class="control">
                      <label class="checkbox">
                        <input type="checkbox" v-model="holiday.is_recurring">
                        Recurring Annually
                      </label>
                    </div>
                  </div>
                </div>
                <div class="column is-1">
                  <button
                      type="button"
                      class="button is-danger is-small"
                      @click="removeHoliday(index)"
                  >
                    <span class="icon"><i class="bx bx-trash"></i></span>
                  </button>
                </div>
              </div>

              <div class="field">
                <div class="control">
                  <button
                      type="button"
                      class="button is-info is-small"
                      @click="addHoliday"
                  >
                    <span class="icon"><i class="bx bx-plus"></i></span>
                    <span>Add Holiday</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Meal Service Times Section -->
          <div class="field">
            <label class="label">Meal Service Times</label>
            <div class="box">
              <div v-for="(mealService, index) in formData.meal_service_times" :key="mealService.id || index" class="columns is-vcentered mb-2">
                <div class="column">
                  <div class="field">
                    <label class="label is-small">Meal Type</label>
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select v-model="mealService.meal_type_id" required>
                          <option v-for="type in mealTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="field">
                    <label class="label is-small">Day of Week</label>
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select v-model="mealService.day_of_week" required>
                          <option v-for="day in weekdays" :key="day.value" :value="day.value">{{ day.label }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="field">
                    <label class="label is-small">Start Time</label>
                    <div class="control">
                      <input type="time" class="input" v-model="mealService.start_time" required>
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="field">
                    <label class="label is-small">End Time</label>
                    <div class="control">
                      <input type="time" class="input" v-model="mealService.end_time" required>
                    </div>
                  </div>
                </div>
                <div class="column is-2">
                  <div class="field">
                    <label class="label is-small">&nbsp;</label>
                    <div class="control">
                      <label class="checkbox">
                        <input type="checkbox" v-model="mealService.is_active">
                        Active
                      </label>
                    </div>
                  </div>
                </div>
                
                <div class="column is-1">
                  <label class="label is-small">&nbsp;</label>
                  <button
                      type="button"
                      class="button is-danger is-small"
                      @click="removeMealServiceTime(index)"
                  >
                    <span class="icon"><i class="bx bx-trash"></i></span>
                  </button>
                </div>
              </div>

              <div class="field">
                <div class="control">
                  <button
                      type="button"
                      class="button is-info is-small"
                      @click="addMealServiceTime"
                  >
                    <span class="icon"><i class="bx bx-plus"></i></span>
                    <span>Add Meal Service Time</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Active Status -->
          <div class="field">
            <div class="control">
              <label class="checkbox">
                <input type="checkbox" v-model="formData.is_active">
                Set as Active Schedule
              </label>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="field is-grouped mt-5">
            <div class="control">
              <button
                  type="submit"
                  class="button is-primary"
                  :class="{ 'is-loading': saving }"
                  :disabled="saving"
              >
                Save Changes
              </button>
            </div>
            <div class="control">
              <router-link :to="`/schedules/${scheduleId}`" class="button is-light">
                Cancel
              </router-link>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';

export default {
  name: 'SiteScheduleEdit',

  data() {
    return {
      scheduleId: null,
      site: {},
      loading: true,
      saving: false,
      mealTypes: [],
      weekdays: [
        { label: 'Sunday', value: 0 },
        { label: 'Monday', value: 1 },
        { label: 'Tuesday', value: 2 },
        { label: 'Wednesday', value: 3 },
        { label: 'Thursday', value: 4 },
        { label: 'Friday', value: 5 },
        { label: 'Saturday', value: 6 }
      ],
      months: [
        { label: 'January', value: 1 },
        { label: 'February', value: 2 },
        { label: 'March', value: 3 },
        { label: 'April', value: 4 },
        { label: 'May', value: 5 },
        { label: 'June', value: 6 },
        { label: 'July', value: 7 },
        { label: 'August', value: 8 },
        { label: 'September', value: 9 },
        { label: 'October', value: 10 },
        { label: 'November', value: 11 },
        { label: 'December', value: 12 }
      ],
      formData: {
        is_active: true,
        working_days: [],
        working_months: [],
        holidays: [],
        meal_service_times: []
      }
    };
  },

  created() {
    this.scheduleId = this.$route.params.scheduleId;
    this.fetchSchedule();
    this.fetchMealTypes();
  },

  methods: {
    ...mapActions({
      showNotification: 'notifications/showNotification'
    }),

    async fetchSchedule() {
      try {
        this.loading = true;

        const response = await axios.get(`/api/schedules/${this.scheduleId}`);
        const schedule = response.data.data;

        // Set site info
        const siteResponse = await axios.get(`/api/sites/${schedule.site_id}`);
        this.site = siteResponse.data;

        // Format dates for form inputs
        const holidays = schedule.holidays.map(holiday => ({
          ...holiday,
          date: holiday.date.split('T')[0] // Format as YYYY-MM-DD
        }));

        // Set form data
        this.formData = {
          is_active: schedule.is_active,
          working_days: schedule.working_days,
          working_months: schedule.working_months,
          holidays: holidays,
          meal_service_times: schedule.meal_service_times || []
        };
      } catch (error) {
        this.showNotification({
          type: 'danger',
          message: 'Failed to load schedule: ' + (error.response?.data?.message || error.message)
        });
      } finally {
        this.loading = false;
      }
    },

    getDayName(dayOfWeek) {
      const day = this.weekdays.find(d => d.value === dayOfWeek);
      return day ? day.label : 'Unknown';
    },

    getMonthName(month) {
      const monthObj = this.months.find(m => m.value === month);
      return monthObj ? monthObj.label : 'Unknown';
    },

    addHoliday() {
      this.formData.holidays.push({
        name: '',
        date: '',
        is_recurring: false
      });
    },

    removeHoliday(index) {
      this.formData.holidays.splice(index, 1);
    },

    async fetchMealTypes() {
      try {
        const response = await axios.get('/api/meal-types');
        this.mealTypes = response.data.data || [];
      } catch (error) {
        this.showNotification({
          type: 'danger',
          message: 'Failed to load meal types: ' + (error.response?.data?.message || error.message)
        });
      }
    },

    addMealServiceTime() {
      this.formData.meal_service_times.push({
        meal_type_id: this.mealTypes.length > 0 ? this.mealTypes[0].id : '',
        day_of_week: 1, // Monday as default
        start_time: '08:00',
        end_time: '09:00',
        is_active: true
      });
    },

    removeMealServiceTime(index) {
      this.formData.meal_service_times.splice(index, 1);
    },

    async saveSchedule() {
      try {
        this.saving = true;

         await axios.put(`/api/schedules/${this.scheduleId}`, this.formData);

        this.showNotification({
          type: 'success',
          message: 'Schedule updated successfully'
        });

        // Redirect to the schedule view
        this.$router.push(`/schedules/${this.scheduleId}`);
      } catch (error) {
        this.showNotification({
          type: 'danger',
          message: 'Failed to update schedule: ' + (error.response?.data?.message || error.message)
        });

        // If there are validation errors, scroll to the top
        if (error.response?.status === 422) {
          window.scrollTo(0, 0);
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
